import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Box, Button, Grid, InputAdornment, Stack, TextField, } from "@mui/material";
import { useAuthoriseBatchPaymentMutationMutation, useBatchPaymentDetailsByBatchPaymentQuery, useBatchPaymentsByIdQuery, useDeleteBatchPaymentMutationMutation, } from "generated/generated-models";
import { Loading } from "app/lib/components/Loading";
import { CError } from "app/common/CError";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { MoneyFormatted } from "app/components/MoneyFormatted";
import NoRowsOverlay from "app/components/NoRowsOverlay";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Edit, Delete } from "@mui/icons-material";
import { DelayedRender } from "app/lib/components/DelayedRender";
import { useState } from "react";
import { ConfirmDialog } from "app/components/ConfirmDialog";
export const ViewBatchPaymentsPage = () => {
    const params = useParams();
    const batchPaymentId = params.id ?? "";
    const { data: batchPaymentDetailsData, loading: batchPaymentDetailsLoading, error: batchPaymentDetailsError, } = useBatchPaymentDetailsByBatchPaymentQuery({
        variables: { input: { BatchPaymentId: batchPaymentId } },
        fetchPolicy: "network-only",
    });
    const { data: batchPaymentData, loading: batchPaymentLoading, error: batchPaymentError, } = useBatchPaymentsByIdQuery({
        variables: { input: { BatchPaymentId: batchPaymentId } },
        fetchPolicy: "network-only",
    });
    if (batchPaymentLoading || batchPaymentDetailsLoading) {
        return _jsx(Loading, {});
    }
    if (batchPaymentError || batchPaymentDetailsError) {
        return _jsx(CError, { error: "Could not retrieve Batch Payment Details" });
    }
    return (_jsx(ViewBatchPayments, { batchPaymentData: batchPaymentData, batchPaymentDetailsData: batchPaymentDetailsData }));
};
const ViewBatchPayments = ({ batchPaymentDetailsData, batchPaymentData, }) => {
    const navigate = useNavigate();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [authoriseBatchPayment, { loading: loadingAuthorise, error: errorAuthorise, data: dataAuthorise, },] = useAuthoriseBatchPaymentMutationMutation();
    const [deleteBatchPayment] = useDeleteBatchPaymentMutationMutation();
    const batchPaymentDetails = batchPaymentDetailsData?.batchPaymentDetails?.BatchPaymentDetails;
    const organisationName = batchPaymentDetails?.find((payment) => payment?.RemitterName !== undefined)?.RemitterName ?? "";
    const columns = [
        {
            field: "Payment",
            headerName: "Payment",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.row.Id) + 1,
        },
        {
            field: "ToAccount",
            headerName: "To Account",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            renderCell: (params) => (_jsxs(Stack, { textAlign: "center", children: [_jsx(Box, { children: params.row.BankIdentifier.BankIdentifier }), _jsx(Box, { children: params.row.AccountNumber })] })),
        },
        {
            field: "AccountName",
            headerName: "Account Name",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "Indicator",
            headerName: "Indicator",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "TransactionCode",
            headerName: "Transaction Code",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "Amount",
            headerName: "Amount",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (_jsx(MoneyFormatted, { amount: params.row.Amount.Amount })),
        },
        {
            field: "LodgementReference",
            headerName: "Lodgement Reference ",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "WithholdingTax",
            headerName: "Withholding Tax",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (_jsx(MoneyFormatted, { amount: params.row.WithholdingTax.Amount })),
        },
    ];
    const status = batchPaymentData?.batchPaymentsById?.Status ?? "";
    const editButtonDisabled = ["Authorised", "Processed"].includes(status);
    const deleteButtonDisabled = ["Processed"].includes(status);
    const authorisedButtonDisabled = !["PendingAuthorisation"].includes(status);
    const handleAuthoriseButton = async () => {
        await authoriseBatchPayment({
            variables: {
                input: {
                    BatchPayment: batchPaymentData?.batchPaymentsById?.Id,
                },
            },
        });
    };
    const handleDeleteBatchPayment = async () => {
        await deleteBatchPayment({
            variables: {
                input: {
                    BatchPaymentId: batchPaymentData?.batchPaymentsById?.Id,
                },
            },
        });
        navigate("/transfersAndPayments/BatchPayments");
    };
    return (_jsx(_Fragment, { children: _jsxs("section", { className: "sub-wrap", children: [_jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 7, children: _jsx("h2", { children: "View Batch Payments" }) }), _jsxs(Grid, { item: true, xs: 5, children: [_jsx(Button, { variant: "text", color: "primary", startIcon: _jsx(Edit, {}), disabled: editButtonDisabled, onClick: () => {
                                                navigate(`/BatchPayments/Edit/${batchPaymentData?.batchPaymentsById?.Id}`);
                                            }, children: "Edit Payment" }), _jsx(Button, { color: "error", disabled: deleteButtonDisabled, startIcon: _jsx(Delete, {}), onClick: () => setConfirmDialogOpen(true), children: "Delete Payment" }), _jsx(Button, { variant: "contained", size: "small", disabled: authorisedButtonDisabled, sx: {
                                                borderRadius: 5,
                                            }, onClick: () => {
                                                handleAuthoriseButton();
                                            }, children: "Authorise" })] })] }), errorAuthorise && (_jsx(CError, { error: "Could not edit batch payment details: " +
                                errorAuthorise.message })), !loadingAuthorise &&
                            dataAuthorise?.authoriseBatchPayment?.Success && (_jsx(Box, { width: "100%", children: _jsxs(Alert, { severity: "success", children: ["Batch Payment Details has been updated successfully", _jsx(DelayedRender, { delay: 1500, children: _jsx(Navigate, { to: "/transfersAndPayments/BatchPayments" }) })] }) }))] }), _jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2, padding: 1, children: [_jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "From Account", InputLabelProps: { shrink: true }, defaultValue: batchPaymentData?.batchPaymentsById?.Account
                                            .Number, disabled: true }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "Organisation Name", InputLabelProps: { shrink: true }, defaultValue: organisationName, disabled: true }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "Scheduled Date", InputLabelProps: { shrink: true }, defaultValue: dayjs(batchPaymentData?.batchPaymentsById
                                            ?.ScheduledDate), disabled: true }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "Description", defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.Description, InputLabelProps: { shrink: true }, disabled: true }) })] }), _jsxs(Grid, { container: true, spacing: 2, padding: 1, children: [_jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { disabled: true, size: "small", defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.PaymentsCount, label: "Total Payments", InputLabelProps: { shrink: true } }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { disabled: true, size: "small", label: "Amount", InputLabelProps: { shrink: true }, inputProps: {
                                            amount: batchPaymentData?.batchPaymentsById
                                                ?.Total?.Amount,
                                        }, InputProps: {
                                            inputComponent: MoneyFormatted,
                                        }, defaultValue: batchPaymentData?.batchPaymentsById?.Total
                                            .Amount }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(TextField, { disabled: true, size: "small", label: "Status", InputLabelProps: {
                                            shrink: true,
                                            disabled: true,
                                        }, defaultValue: batchPaymentData?.batchPaymentsById?.Status.replace(/([A-Z])/g, " $1").trim() }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(TextField, { disabled: true, defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.AuthorisedByName, size: "small", label: "Authorised By", InputLabelProps: { shrink: true }, InputProps: {
                                            endAdornment: (_jsx(InputAdornment, { position: "end", children: dayjs(batchPaymentData
                                                    ?.batchPaymentsById
                                                    ?.AuthorisedDate).format("DD/MM/YYYY") })),
                                        } }) })] })] }), _jsx(Box, { padding: 1, children: _jsx(DataGridPro, { className: "custom-grid", columns: columns, rows: batchPaymentDetails ?? [], getRowId: (r) => r.Id, pageSizeOptions: [10, 25, 50], autoHeight: true, pagination: true, initialState: {
                            pagination: {
                                paginationModel: { pageSize: 10 },
                            },
                        }, slots: {
                            noRowsOverlay: () => (_jsx(NoRowsOverlay, { children: "There are currently no batch payment details for this batch payment." })),
                        }, disableColumnSelector: true }) }), _jsx(Box, { display: "flex", alignItems: "center", height: "100%", flexWrap: "wrap", columnGap: 2, rowGap: 1, justifyContent: "flex-start", children: _jsx(Button, { variant: "outlined", color: "secondary", onClick: () => navigate(-1), children: "Cancel" }) }), _jsx(ConfirmDialog, { title: "Delete Payment", description: `Are you sure you want to delete the following batch payment, '${batchPaymentData?.batchPaymentsById?.Description}'?`, open: confirmDialogOpen, onCancel: () => {
                        setConfirmDialogOpen(false);
                    }, onConfirm: () => {
                        handleDeleteBatchPayment();
                        setConfirmDialogOpen(false);
                    }, note: "The process cannot be undone" })] }) }));
};
